import * as Yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { createObjectFromKey } from '../../../../helpers/formHelpers';
import { FormFieldsRecord } from '../../../../helpers/formHelpers/types';
import { FetchCompanyOutletsResponse } from '../../../../services/Company/useFetchCompanyOutlets';
import { UobPointRedemptionDetailsViaMerchantConnectResponse } from '../../../../services/UOBPointRedemption/useFetchUOBPointRedemptionDetails';

export type CreateUpdateUobPointRedemptionForm = {
  mdr_percentage: number;
  acquired_by: string;
  outlet_ids: number[];
  remarks?: string;
};

const acquiredByOptions = [
  { key: 'fave', label: 'Fave', value: 'fave' },
  { key: 'tpa', label: 'TPA', value: 'tpa' },
  { key: 'sales_agent', label: 'Sales Agent', value: 'sales_agent' },
];

export const useUobPointRedemptionFormBuilder = ({
  companyOutlets,
  uobPointRedemptionDetails,
}: ApiResponse) => {
  const formFields: FormFieldsRecord = {
    mdr_percentage: {
      label: 'MDR Percentage (%)',
      type: 'number',
      key: 'mdr_percentage',
      col: 12,
      value: uobPointRedemptionDetails?.product_details.mdr_percentage,
      precision: 2,
      schema: Yup.number()
        .min(0, 'Minimum is 0')
        .max(100, 'Maximum is 100')
        .typeError('Required')
        .required('Required'),
      required: true,
    },
    acquired_by: {
      label: 'Acquired By',
      type: 'select',
      key: 'acquired_by',
      options: acquiredByOptions,
      col: 12,
      value: uobPointRedemptionDetails?.product_details.acquired_by,
      schema: Yup.string().required('Required'),
      required: true,
    },
    outlet_ids: {
      label: 'Apply to Outlet(s)',
      type: 'select',
      key: 'outlet_ids',
      options:
        (companyOutlets?.outlets
          .filter(item => item.status === 'active')
          .map(item => ({
            key: item.id,
            label: item.name,
            value: item.id,
          })) as []) || [],
      mode: 'multiple',
      col: 24,
      value: uobPointRedemptionDetails?.product_details.outlet_ids,
      schema: Yup.array()
        .of(Yup.string())
        .min(1, 'Must select at least one outlet.')
        .required('Must select at least one outlet.'),
      required: true,
    },
    remarks: {
      label: 'Remarks',
      type: 'textarea',
      key: 'remarks',
      value: uobPointRedemptionDetails?.product_details.remarks,
      schema: Yup.string().nullable(),
      rows: 4,
      placeholder: 'Optional remarks or notes...',
    },
  };

  const initialValues = createObjectFromKey(
    formFields,
    'value' as keyof FormFieldsRecord[keyof FormFieldsRecord],
  ) as unknown as CreateUpdateUobPointRedemptionForm;

  const formSchema = Yup.object().shape(
    createObjectFromKey(formFields, 'schema') as ObjectShape,
  );

  return { formFields, initialValues, formSchema };
};

type ApiResponse = {
  companyOutlets?: FetchCompanyOutletsResponse;
  uobPointRedemptionDetails?: UobPointRedemptionDetailsViaMerchantConnectResponse;
};
